import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {WhereWeFlyCopy, WhereWeFlyTiles} from "@models/cms/cms-dynamic/experience/where-we-fly.model";
import {SvgIconComponent} from "@components/svg-icon/svg-icon.component";
import {CityPair} from "@models/cms/cms-dynamic/experience/experience.model";
import {
  availabilityGetRouteSchedule,
  availabilitySearchFromDestinationsWithSchedule
} from "@actions/availability.action";
import {PassengerTypeCode} from "@models/booking.models";
import {FALLBACK_CURRENCY} from "@constants/app.constants";
import {TripType} from "@constants/home.constants";
import {Store} from "@ngrx/store";
import {AsyncPipe, JsonPipe} from "@angular/common";
import {selectSelectedCurrencyCode} from "@selectors/culture.selector";
import {LetDirective} from "@directives/app-let.directive";
import {GrowthBookService} from "@services/growthbook/growthbook.service";
import {UtilitiesService} from "@services/utilities/utilities.service";
import {TooltipService} from "@components/tooltip/tooltip.service";
import {NgClickOutsideDirective} from "ng-click-outside2";
import {Image} from "@models/cms/cms-dynamic/image.model";
import {DATE_FORMAT_YEAR_MONTH_DATE} from "@constants/date-format.constants";
import {SearchWidgetCopy} from "@models/cms/search-widget.model";
import {DateTime} from "luxon";
import {scheduleLoadedThru} from "@utils/availability.utils";
import {take} from "rxjs/operators";
import {selectRouteSchedule} from "@selectors/availability.selector";

@Component({
    selector: 'app-destination-map-tooltip',
    standalone: true,
  imports: [
    SvgIconComponent,
    AsyncPipe,
    LetDirective,
    NgClickOutsideDirective,
    JsonPipe
  ],
    templateUrl: './destination-map-tooltip.component.html',
    styleUrl: './destination-map-tooltip.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DestinationMapTooltipComponent {
    @Input() content: DestinationMapTooltipContent | undefined;

    selectedCurrency$ = this.store.select(selectSelectedCurrencyCode);
  routeSchedule$ = this.store.select(selectRouteSchedule);

    get image(): Image | undefined {
      return this.content?.whereWeFlyCopy?.images?.map(image => {
        if (image[`destinationImage${this.content?.stationCode}`]) {
          return image[`destinationImage${this.content?.stationCode}`];
        }
      }).filter(item => item)[0] as Image;
    }

    get destinations(): CityPair[] | undefined {
        if (this.content && this.content.stationCode) {
            return this.content?.whereWeFlyCopy?.routesCities?.flatMap(routesCities => {
                return routesCities.cityPairs.flatMap(cityPair => {
                    if (cityPair.destinationCode === this.content?.stationCode || cityPair.originCode === this.content?.stationCode) {
                        return cityPair;
                    }
                })
            }).filter(item => item) as CityPair[];
        }
    }

    get tileContent(): WhereWeFlyTiles | undefined {
        if (this.content && this.content.stationCode) {
            return this.content.whereWeFlyCopy?.tiles?.find(tile => {
                return tile.code === this.content?.stationCode
            });
        }
    }

    constructor(private store: Store,
                public gbs: GrowthBookService,
                private utilitiesService: UtilitiesService,
                private tooltipService: TooltipService,
    ) {
    }

    searchForFlights(destination: string, currency: string | null) {
        this.closeTooltip();

        if (currency) {
            const originStationCode = destination;
            const destinationStationCode = this.content?.stationCode || '';
          if (originStationCode && destinationStationCode) {
            this.routeSchedule$.pipe(take(1)).subscribe((s) => {
              if (!s?.[`${originStationCode}-${destinationStationCode}`]) {
                if (originStationCode !== destinationStationCode) {
                  this.store.dispatch(
                    availabilityGetRouteSchedule({
                      request: {
                        origin: originStationCode,
                        destination: destinationStationCode,
                        beginDate: DateTime.now()
                          .startOf('day')
                          .toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
                        endDate: scheduleLoadedThru(this.content?.cmsCopy) || '',
                      },
                    }),
                  );
                }
              }
                if (!s?.[`${destinationStationCode}-${originStationCode}`]) {
                  this.store.dispatch(
                    availabilityGetRouteSchedule({
                      request: {
                        origin: destinationStationCode,
                        destination: originStationCode,
                        beginDate: DateTime.now()
                          .startOf('day')
                          .toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
                        endDate: scheduleLoadedThru(this.content?.cmsCopy) || ''
                      },
                    }),
                  );
                }
            });
          }

          this.store.dispatch(availabilitySearchFromDestinationsWithSchedule({
                payload: {
                    criteria: [
                        {
                            beginDate: new Date().toISOString(),
                            destination: this.content?.stationCode || '',
                            origin: destination,
                            endDate: scheduleLoadedThru(this.content?.cmsCopy)
                        },
                        {
                            beginDate: new Date().toISOString(),
                            origin: this.content?.stationCode || '',
                            destination: destination,
                            endDate: scheduleLoadedThru(this.content?.cmsCopy)
                        }
                    ],
                    passengers: [
                        {
                            count: 1,
                            type: PassengerTypeCode.ADT
                        }
                    ],
                    childDobs: [],
                    infantDobs: [],
                    currencyCode: currency ?? FALLBACK_CURRENCY,
                    clearBooking: true,
                    lowFare: "true",
                },
                tripType: TripType.roundTrip,
            }));
            this.utilitiesService.updateFlightSearchActionStarted();
        }
    }

    closeTooltip() {
        this.tooltipService.hideTooltip(true, true);
    }
}

export interface DestinationMapTooltipContent {
  cityName?: string;
  cmsCopy?: SearchWidgetCopy | null | undefined;
  stationCode?: string;
  whereWeFlyCopy?: WhereWeFlyCopy | null | undefined;
}
